import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { fmt, initials, monthShort } from "./financeUtils";

/**
 * Üye Ödemeleri · Ay Ay (artifact `.mem-card` + `.mgrid`).
 * members: [{ id, name, packageName, orgName, months: [{key,date,status,amount,remaining}] , currentStatus }]
 *   status: 'paid' | 'part' | 'unpaid' | 'none'
 * The month grid is horizontally scrollable; legend at card bottom.
 */
const STATUS_BADGE = {
  paid: { cls: "bg-[var(--fin-pos-soft)] text-[var(--fin-pos)]", label: "Ödendi" },
  part: { cls: "bg-[var(--fin-warn-soft)] text-[var(--fin-warn)]", label: "Kısmi" },
  unpaid: { cls: "bg-[var(--fin-neg-soft)] text-[var(--fin-neg)]", label: "Ödenmedi" },
  none: { cls: "bg-[var(--fin-chipbg)] text-[var(--fin-muted)]", label: "—" },
};

const DOT_COLOR = {
  paid: "var(--fin-pos)",
  part: "var(--fin-warn)",
  unpaid: "var(--fin-neg)",
  none: "#c4cbc8",
};

const AMT_COLOR = {
  paid: "var(--fin-pos)",
  part: "var(--fin-warn)",
  unpaid: "var(--fin-neg)",
  none: "var(--fin-muted)",
};

const MemberCard = ({ m, selectedMonthShort }) => {
  const [open, setOpen] = useState(false);
  const badge = STATUS_BADGE[m.currentStatus] || STATUS_BADGE.none;

  return (
    <div className="border-b border-[var(--fin-line)] last:border-b-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-[11px] px-4 py-3 hover:bg-[var(--fin-hover)] transition-colors text-left"
      >
        <div className="fin-grot w-[38px] h-[38px] rounded-[11px] bg-[var(--fin-track)] text-[var(--fin-text)] grid place-items-center font-semibold text-[13px] shrink-0">
          {initials(m.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold text-[var(--fin-text)] truncate">
            {m.name}
          </div>
          <div className="text-[11px] text-[var(--fin-muted)] truncate">
            {[m.packageName, m.orgName].filter(Boolean).join(" · ")}
          </div>
        </div>
        <span
          className={`shrink-0 text-[10px] font-semibold px-[7px] py-0.5 rounded-full ${badge.cls}`}
        >
          {selectedMonthShort}: {badge.label}
        </span>
        <span className={`fin-chev text-[var(--fin-muted)] text-[12px] ${open ? "open" : ""}`}>
          ▾
        </span>
      </button>

      <div className={`fin-collapse ${open ? "open" : ""}`}>
        <div className="bg-[var(--fin-hover)]">
          <div className="flex gap-[7px] px-4 pt-1 pb-3.5 overflow-x-auto no-scrollbar">
            {m.months.map((cell) => (
              <div
                key={cell.key}
                title={cell.status === "part" && cell.remaining ? `Kalan: ${fmt(cell.remaining)}` : undefined}
                className={`min-w-[64px] bg-[var(--fin-card)] border border-[var(--fin-line)] rounded-[10px] px-1.5 py-2 text-center shrink-0 ${
                  cell.status === "none" ? "opacity-40" : ""
                }`}
              >
                <div className="text-[10px] font-semibold text-[var(--fin-muted)] uppercase tracking-[0.05em]">
                  {cell.label}
                </div>
                <div
                  className="w-[9px] h-[9px] rounded-full mx-auto mt-1.5 mb-[5px]"
                  style={{ background: DOT_COLOR[cell.status] }}
                />
                <div
                  className="fin-grot fin-num font-semibold text-[10.5px]"
                  style={{ color: AMT_COLOR[cell.status] }}
                >
                  {cell.amount > 0 ? fmt(cell.amount) + (cell.status === "part" ? "↧" : "") : "—"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MemberPayments = ({ members, monthDate }) => {
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(10);
  const selShort = monthShort(monthDate);

  const filtered = useMemo(() => {
    if (!query.trim()) return members;
    const q = query.trim().toLocaleLowerCase("tr-TR");
    return members.filter((m) => m.name.toLocaleLowerCase("tr-TR").includes(q));
  }, [members, query]);

  const visible = filtered.slice(0, limit);

  return (
    <section>
      <div className="flex justify-between items-center gap-2 mx-1 mb-2">
        <h2 className="fin-grot text-[14px] font-semibold text-[var(--fin-text)] whitespace-nowrap">
          Üye Ödemeleri · Ay Ay
        </h2>
        <span className="text-[11px] text-[var(--fin-muted)] hidden sm:inline">
          üyeye dokun → 6 ay
        </span>
      </div>
      <div className="fin-card">
        {/* search */}
        <div className="relative border-b border-[var(--fin-line)]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--fin-muted)]" />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setLimit(10);
            }}
            placeholder="Üye ara..."
            className="w-full bg-transparent pl-10 pr-4 py-2.5 text-[13px] text-[var(--fin-text)] placeholder-[var(--fin-muted)] outline-none"
          />
        </div>

        {visible.length ? (
          visible.map((m) => (
            <MemberCard key={m.id} m={m} selectedMonthShort={selShort} />
          ))
        ) : (
          <div className="px-4 py-[26px] text-center text-[12.5px] text-[var(--fin-muted)]">
            Bu kapsamda üye yok.
          </div>
        )}

        {filtered.length > limit && (
          <button
            onClick={() => setLimit((l) => l + 20)}
            className="w-full py-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--fin-blue)] hover:bg-[var(--fin-hover)] transition-colors border-t border-[var(--fin-line)]"
          >
            Daha fazla göster (+{filtered.length - limit})
          </button>
        )}

        {/* legend */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 px-4 py-3 text-[10.5px] text-[var(--fin-muted)] border-t border-[var(--fin-line)]">
          <span>
            <i className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: "var(--fin-pos)" }} />
            Ödendi
          </span>
          <span>
            <i className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: "var(--fin-warn)" }} />
            Kısmi
          </span>
          <span>
            <i className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: "var(--fin-neg)" }} />
            Ödenmedi
          </span>
          <span>
            <i className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: "#c4cbc8" }} />
            Üyelik yok
          </span>
        </div>
      </div>
    </section>
  );
};

export default MemberPayments;
